/**
 * Page fiche espèce — vue détaillée éditoriale.
 */

import { el, clear } from '../js/utils.js';
import { t, getLang } from '../js/i18n.js';
import {
  getSpecies,
  formatSize,
  formatDepthRange,
  dangerLabel,
  iucnLabel,
  pickField,
} from '../js/species.js';
import { renderHeader } from '../components/header.js';
import { renderFooter } from '../components/footer.js';

export async function renderSpeciesDetail(root, { params, query = {} }) {
  clear(root);
  root.append(renderHeader());
  root.append(
    el(
      'main',
      { class: 'detail-page container', id: 'detail-root' },
      el('p', { class: 'muted', style: { paddingBlock: 'var(--space-7)' } }, t('common.loading'))
    )
  );
  root.append(renderFooter());

  let species;
  try {
    species = await getSpecies(params.id);
  } catch (err) {
    root.querySelector('#detail-root').replaceWith(
      el(
        'main',
        { class: 'container', style: { paddingBlock: 'var(--space-7)', textAlign: 'center' } },
        el('h1', {}, t('detail.notFound.title')),
        el(
          'p',
          { class: 'muted', style: { marginBlock: 'var(--space-4)' } },
          t('detail.notFound.body')
        ),
        el('a', { href: '#/species', class: 'btn btn-ghost' }, t('detail.notFound.back'))
      )
    );
    console.error(err);
    return;
  }

  const photos = (species.species_images || [])
    .filter((i) => i.url)
    .sort((a, b) => {
      if (a.is_primary !== b.is_primary) return b.is_primary ? 1 : -1;
      return (a.display_order ?? 0) - (b.display_order ?? 0);
    });

  const taxClass =
    species.taxonomy?.class || species.taxonomy?.phylum || t('detail.fallback.taxClass');

  // Nom d'affichage selon la langue
  const lang = getLang();
  const nameDisplay =
    lang === 'en' && species.common_name_en
      ? species.common_name_en
      : species.common_name_fr || species.scientific_name;

  const view = el(
    'main',
    { class: 'detail-page' },

    // ---------- Hero ----------
    el('section', { class: 'detail-hero container-wide' }, ...buildHero(photos, nameDisplay)),

    // ---------- Titre éditorial ----------
    el(
      'header',
      { class: 'detail-header container' },
      el('p', { class: 'eyebrow', style: { color: 'var(--coral)' } }, taxClass),
      el('h1', { class: 'detail-name-fr' }, nameDisplay),
      el(
        'p',
        { class: 'detail-name-sci' },
        el('em', {}, species.scientific_name),
        species.common_name_en && lang === 'fr'
          ? el('span', { class: 'muted' }, ` · ${species.common_name_en}`)
          : null
      ),
      species.is_invasive &&
        el('p', { class: 'detail-invasive-badge' }, t('species.invasive'))
    ),

    // ---------- Lead + facts ----------
    el(
      'section',
      { class: 'detail-body container' },
      pickField(species, 'description_short') &&
        el('p', { class: 'detail-lead' }, pickField(species, 'description_short')),

      el('div', { class: 'detail-facts' }, ...buildFacts(species)),

      ...buildLongDescription(species),
      ...buildSections(species),

      pickField(species, 'danger_notes') &&
        el(
          'div',
          { class: 'detail-danger-card' },
          el('p', { class: 'eyebrow', style: { color: 'var(--rust)' } }, t('detail.danger.eyebrow')),
          el('p', {}, pickField(species, 'danger_notes'))
        ),

      species.taxonomy && el('hr', { class: 'divider' }),
      species.taxonomy &&
        el(
          'div',
          { class: 'detail-taxonomy' },
          el('p', { class: 'eyebrow' }, t('detail.taxonomy.eyebrow')),
          el(
            'p',
            { class: 'muted' },
            ['kingdom', 'phylum', 'class', 'order', 'family', 'genus']
              .map((rank) => species.taxonomy[rank])
              .filter(Boolean)
              .join(' › ')
          )
        ),

      ...buildExternalLinks(species)
    )
  );

  root.querySelector('#detail-root').replaceWith(view);
}

// ---------- Hero photo + galerie ----------

function buildHero(photos, nameDisplay) {
  if (photos.length === 0) return [];

  const mainImg = el('img', { src: photos[0].url, alt: nameDisplay, id: 'hero-img' });
  const credit = el('figcaption', { class: 'detail-photo-credit', id: 'hero-credit' });
  updateCredit(credit, photos[0]);

  const figure = el('figure', { class: 'detail-photo' }, mainImg, credit);
  const result = [figure];

  if (photos.length > 1) {
    const gallery = el('div', { class: 'detail-gallery' });
    photos.forEach((photo, i) => {
      const thumb = el(
        'button',
        {
          class: `detail-thumb ${i === 0 ? 'is-active' : ''}`,
          'aria-label': t('detail.gallery.aria', { n: i + 1, total: photos.length }),
          onclick: () => {
            mainImg.src = photo.url;
            updateCredit(credit, photo);
            gallery
              .querySelectorAll('.detail-thumb')
              .forEach((b) => b.classList.remove('is-active'));
            thumb.classList.add('is-active');
          },
        },
        el('img', { src: photo.thumbnail_url || photo.url, alt: '', loading: 'lazy' })
      );
      gallery.append(thumb);
    });
    result.push(gallery);
  }

  return result;
}

function updateCredit(node, photo) {
  node.innerHTML = '';
  if (!photo) return;
  node.append(t('detail.photo.credit') + ' ');
  if (photo.attribution) {
    if (photo.source_url) {
      node.append(
        el('a', { href: photo.source_url, target: '_blank', rel: 'noopener nofollow' }, photo.attribution)
      );
    } else {
      node.append(photo.attribution);
    }
  }
  if (photo.license) {
    node.append(el('span', { class: 'detail-photo-license' }, ` · ${photo.license}`));
  }
}

// ---------- Description longue (Wikipédia) ----------

function buildLongDescription(species) {
  const lang = getLang();
  const content =
    lang === 'en' && species.description_long_en
      ? species.description_long_en
      : species.description_long;

  if (!content || content.trim().length < 30) return [];

  const container = el('div', { class: 'detail-rich' });
  container.innerHTML = content;
  container.querySelectorAll('a').forEach((a) => {
    a.target = '_blank';
    a.rel = 'noopener nofollow';
  });

  // URL Wikipédia selon la langue (wikipedia_en stocké dans external_ids si disponible)
  const wikiUrl =
    lang === 'en'
      ? species.external_ids?.wikipedia_en || species.external_ids?.wikipedia
      : species.external_ids?.wikipedia;

  const attribution = el(
    'p',
    { class: 'detail-rich-credit' },
    t('detail.wikipedia.source') + ' ',
    wikiUrl
      ? el('a', { href: wikiUrl, target: '_blank', rel: 'noopener' }, t('detail.wikipedia.label'))
      : t('detail.wikipedia.label'),
    ' ',
    t('detail.wikipedia.license'),
    ' ',
    el(
      'a',
      {
        href: 'https://creativecommons.org/licenses/by-sa/4.0/',
        target: '_blank',
        rel: 'noopener',
      },
      'CC BY-SA 4.0'
    )
  );

  return [container, attribution];
}

// ---------- Bloc des faits ----------

function buildFacts(species) {
  const facts = [];

  const size = formatSize(species.max_size_cm);
  if (size) facts.push(fact(t('detail.fact.size'), size));

  const depth = formatDepthRange(species.depth_min_m, species.depth_max_m);
  if (depth) facts.push(fact(t('detail.fact.depth'), depth));

  if (species.typical_depth) {
    facts.push(fact(t('detail.fact.typical_depth'), t(`typical_depth.${species.typical_depth}`)));
  }

  if (species.observations_count != null) {
    facts.push(fact(
      t('detail.fact.observations'),
      t('detail.fact.observations.value', { count: species.observations_count.toLocaleString() })
    ));
  }

  if (species.danger_level != null) {
    facts.push(
      el(
        'div',
        { class: 'detail-fact' },
        el('p', { class: 'detail-fact-label' }, t('detail.fact.danger')),
        el(
          'p',
          { class: 'detail-fact-value' },
          el('span', { class: 'danger-dots' }, ...buildDangerDots(species.danger_level))
        ),
        el('p', { class: 'detail-fact-sub muted' }, dangerLabel(species.danger_level))
      )
    );
  }

  if (species.conservation_status) {
    facts.push(
      el(
        'div',
        { class: 'detail-fact' },
        el('p', { class: 'detail-fact-label' }, t('detail.fact.iucn')),
        el(
          'p',
          { class: 'detail-fact-value' },
          el(
            'span',
            { class: `iucn-badge iucn-${species.conservation_status}` },
            species.conservation_status
          )
        ),
        el('p', { class: 'detail-fact-sub muted' }, iucnLabel(species.conservation_status))
      )
    );
  }

  return facts;
}

function fact(label, value) {
  return el(
    'div',
    { class: 'detail-fact' },
    el('p', { class: 'detail-fact-label' }, label),
    el('p', { class: 'detail-fact-value' }, value)
  );
}

function buildDangerDots(level) {
  return Array.from({ length: 5 }, (_, i) =>
    el('span', { class: `danger-dot ${i < level ? 'is-on' : ''}` })
  );
}

// ---------- Sections (habitat, diet, reproduction) ----------

function buildSections(species) {
  const sections = [];
  const map = [
    [t('detail.section.habitat'), pickField(species, 'habitat')],
    [t('detail.section.diet'), pickField(species, 'diet')],
    [t('detail.section.reproduction'), pickField(species, 'reproduction')],
  ];
  for (const [label, value] of map) {
    if (value) {
      sections.push(
        el(
          'div',
          { class: 'detail-section' },
          el('p', { class: 'eyebrow' }, label),
          el('p', {}, value)
        )
      );
    }
  }
  return sections;
}

// ---------- Liens externes ----------

function buildExternalLinks(species) {
  const ext = species.external_ids || {};
  const links = [];
  if (ext.gbif) {
    links.push(
      el(
        'a',
        { href: `https://www.gbif.org/species/${ext.gbif}`, target: '_blank', rel: 'noopener' },
        'GBIF'
      )
    );
  }
  if (ext.inaturalist) {
    links.push(
      el(
        'a',
        {
          href: `https://www.inaturalist.org/taxa/${ext.inaturalist}`,
          target: '_blank',
          rel: 'noopener',
        },
        'iNaturalist'
      )
    );
  }
  const wikiUrl =
    getLang() === 'en'
      ? ext.wikipedia_en || ext.wikipedia
      : ext.wikipedia;
  if (wikiUrl) {
    links.push(
      el('a', { href: wikiUrl, target: '_blank', rel: 'noopener' }, t('detail.wikipedia.label'))
    );
  }

  if (links.length === 0) return [];
  return [
    el('hr', { class: 'divider' }),
    el(
      'div',
      { class: 'detail-external' },
      el('p', { class: 'eyebrow' }, t('detail.external.eyebrow')),
      el('p', { class: 'detail-external-links' }, ...interleave(links, ' · '))
    ),
  ];
}

function interleave(arr, sep) {
  return arr.flatMap((item, i) => (i === 0 ? [item] : [sep, item]));
}
